import { ServiceError } from '../errors'
import { readLocal, writeLocal } from '../local-store'
import { MOCK_SLOW_MS, simulate } from '../mock-utils'
import { PAYMENT_POLL_MS, PAYMENT_WAIT_MS, sleep } from '../pagos/flow'
import { readMockBankDecision } from '../pagos/mock'
import type { Phase } from '../reserva/types'
import { money } from '../types'
import { DISMISSED_FIELD } from './real'
import type { CartItem, Extra, ExtraOrder, ExtrasCheckoutSummary, ExtrasService, Recommendation } from './types'

/** Forma del HTML del widget de Civitatis (display=cosy): tarjetas con enlace relativo, imagen y precio. */
export const MOCK_CIVITATIS_WIDGET = `<link rel="stylesheet" href="https://www.civitatis.com/widget.css"><style>.cosy-card{color:red}</style><script>alert(1)</script>
<div class="o-main-section"><ul class="o-main-list">
<li class="o-main-list__item"><div class="cosy-card"><a href="/es/madrid/tour-bernabeu/" data-gtm-new-model-click="1" onclick="track()"><picture><source srcset="/f/madrid/tour-bernabeu.webp" type="image/webp"><img src="https://www.civitatis.com/f/madrid/tour-bernabeu.jpg" alt="Tour del Bernabéu" style="width:900px"></picture><div class="cosy-card__content"><h3 class="cosy-card__title">Tour por el estadio Santiago Bernabéu</h3><div class="__right"><span class="cosy-card__price">25 €</span><span class="cosy-card__button">Reservar</span></div></div></a></div></li>
<li class="o-main-list__item"><div class="cosy-card"><a href="/es/madrid/free-tour-madrid/"><img src="https://www.civitatis.com/f/madrid/free-tour.jpg" alt="Free tour por Madrid"><div class="cosy-card__content"><h3 class="cosy-card__title">Free tour por el Madrid de los Austrias</h3><div class="__right"><span class="cosy-card__price">Gratis</span></div></div></a></div></li>
<li class="o-main-list__item"><div class="cosy-card"><a href="javascript:alert(1)"><img src="http://inseguro.example/x.jpg" alt=""><div class="cosy-card__content"><h3 class="cosy-card__title">Excursión a Toledo</h3></div></a></div></li>
</ul></div>`

export const MOCK_CIVITATIS_EMPTY = '<div class="o-main-section"><ul class="o-main-list"></ul></div>'

/**
 * Mock de extras con el contrato propuesto (`POST extras/checkout`, docs/backend/extras.md): cobro en la app con
 * tarjeta guardada, recibo por extra e información práctica. Códigos:
 *  - `extras-vacio` → catálogo vacío · `extras-falla` → el checkout falla antes de cobrar
 *  - el cobro usa los del banco simulado: `pago-ok`, `pago-rechazado`, `pago-tiempo`, `pago-directo` (sin banco)
 */
const base = { photoUrl: null, availability: null, alreadyAdded: false, partner: null, when: null, kind: 'extra' as const }

/** Extras de PantallaExtras.dc.html (Bloque 6 §6a). */
export const MOCK_EXTRAS: Extra[] = [
  { ...base, id: 'E-TRANSFER', name: 'Transfer desde el aeropuerto', description: 'Te recoge en la terminal a la hora de tu vuelo. Hasta 3 personas con maletas.', price: money(4500), priceUnit: 'total', conditions: 'Se cancela gratis hasta 24 h antes.', when: '2026-10-03T14:35:00+02:00' },
  { ...base, id: 'E-BREAKFAST', name: 'Desayuno', description: 'Se deja en el ático a las 8:30: bollería, fruta, zumo y café. Precio para 3 personas.', price: money(1200), priceUnit: 'per_person', conditions: 'Se cancela gratis hasta las 22:00 del día anterior.', when: '2026-10-04T08:30:00+02:00' },
  { ...base, id: 'E-LATE', name: 'Salir más tarde', kind: 'late_checkout', description: 'Sal hasta las 14:00 en vez de a las 11:00. Sujeto a disponibilidad.', price: money(2500), priceUnit: 'total', conditions: null },
  { ...base, id: 'E-WINE', name: 'Cata de vinos', description: 'Con nuestro partner Bodega La Latina, a 5 minutos. Precio para 3 personas.', price: money(3500), priceUnit: 'per_person', conditions: 'Se cancela gratis hasta 24 h antes.', partner: 'Bodega La Latina' },
  { ...base, id: 'E-CRIB', name: 'Cuna', description: 'Cuna de viaje con sábanas. Te la dejamos montada.', price: money(0), priceUnit: 'total', conditions: null },
]

/** Información práctica de después (Servicios.dc.html cartDone). */
const PRACTICAL: Record<string, string> = {
  'E-TRANSFER': 'Terminal 4, salida de llegadas. El conductor os escribe por WhatsApp.',
  'E-BREAKFAST': 'Se deja en la puerta del ático.',
  'E-CRIB': 'Montada a vuestra llegada.',
  'E-WINE': 'Bodega La Latina, calle de la Cava Baja 12. Preguntad por Lucía.',
}

/** Una recomendación por fase, con los textos del bundle (Inicio.dc.html). */
const RECOMMENDATIONS: Partial<Record<Phase, Recommendation>> = {
  before: { id: 'RC-1', extraId: 'E-TRANSFER', reason: 'Llegas en 17 días · en metro', title: '¿Transfer desde el aeropuerto?', text: 'Os recoge a los tres en la terminal, con maletas. Puedes añadirlo hasta el día antes.', price: money(4500), priceUnit: 'total', actionLabel: 'Ver el transfer' },
  arrival: { id: 'RC-2', extraId: 'E-BREAKFAST', reason: 'Para mañana', title: 'Desayuno en el ático a las 8:30', text: 'Bollería, fruta, zumo y café para los tres. Pídelo antes de las 22:00.', price: money(1200), priceUnit: 'per_person', actionLabel: 'Pedir desayuno' },
  during: { id: 'RC-3', extraId: 'E-WINE', reason: 'Esta tarde, a 5 minutos', title: 'Cata de vinos en Bodega La Latina', text: 'Una hora, cinco vinos y algo de picar. Hay plazas a las 19:00.', price: money(3500), priceUnit: 'per_person', actionLabel: 'Ver la cata' },
  checkout: { id: 'RC-4', extraId: 'E-LATE', reason: '¿Necesitáis más tiempo?', title: 'Salir más tarde, hasta las 14:00', text: 'Disponible hoy. Se cobra a la tarjeta guardada y recibís el recibo.', price: money(2500), priceUnit: 'total', actionLabel: 'Ver horas y precio' },
}

/** Hotel Mirador del Puerto (PantallaEstancia.dc.html). */
const RECOMMENDATIONS_HOTEL: Partial<Record<Phase, Recommendation>> = {
  before: { id: 'RC-H1', extraId: 'E-BREAKFAST', reason: 'Llegas en 17 días', title: 'Desayuno en la habitación', text: 'Servido de 8:00 a 10:30. Se paga al salir.', price: money(1200), priceUnit: 'per_person', actionLabel: 'Añadir' },
}

const RECEIPTS_EMAIL = 'marta.ruiz@gmail.com'
const CHECKOUTS_FIELD = 'mockExtrasCheckouts'

interface MockCheckout {
  id: string
  orders: ExtraOrder[]
  paid: boolean
  temporalPaidId: string | null
}

const readCheckouts = (guestCode: string) => readLocal<MockCheckout[]>(guestCode, CHECKOUTS_FIELD) ?? []
const findCheckout = (guestCode: string, id: string) => readCheckouts(guestCode).find((c) => c.id === id) ?? null
function saveCheckout(guestCode: string, checkout: MockCheckout): void {
  writeLocal(guestCode, CHECKOUTS_FIELD, [checkout, ...readCheckouts(guestCode).filter((c) => c.id !== checkout.id)])
}

/** Extras ya contratados: los de checkouts pagados, que el catálogo marca como añadidos. */
const addedIds = (guestCode: string) => new Set(readCheckouts(guestCode).filter((c) => c.paid).flatMap((c) => c.orders.map((o) => o.extraId)))

function orderFor(item: CartItem, index: number): ExtraOrder {
  const extra = MOCK_EXTRAS.find((e) => e.id === item.extraId)
  if (!extra) throw new ServiceError('server', `extra ${item.extraId} not found`)
  const qty = extra.priceUnit === 'total' ? 1 : Math.max(1, item.quantity)
  return { id: `ORD-${Date.now()}-${index}`, extraId: extra.id, name: extra.name, when: extra.when, amount: money(extra.price.amount * qty, extra.price.currency), practicalInfo: PRACTICAL[extra.id] ?? null, receiptId: `REC-${Date.now()}-${index}` }
}

export const extrasMock: ExtrasService = {
  paysInApp: true,
  // `civitatis` simula una reserva de Renthas; `civitatis-falla` y `civitatis-vacio`, el widget que falla o no trae nada.
  partnerProvider: (stay) => (stay.guestCode.includes('civitatis') ? 'civitatis' : null),
  async getPartnerWidget(stay, _lang, signal) {
    await simulate(stay.guestCode, 'extras', signal)
    if (!stay.guestCode.includes('civitatis')) throw new ServiceError('not_supported')
    if (stay.guestCode.includes('civitatis-falla')) throw new ServiceError('server', 'mock civitatis')
    if (stay.guestCode.includes('civitatis-vacio')) return MOCK_CIVITATIS_EMPTY
    return MOCK_CIVITATIS_WIDGET
  },
  async list(stay, signal) {
    await simulate(stay.guestCode, 'extras', signal)
    if (stay.guestCode.includes('extras-vacio')) return []
    const added = addedIds(stay.guestCode)
    return MOCK_EXTRAS.map((e) => (added.has(e.id) ? { ...e, alreadyAdded: true } : e))
  },
  async getRecommendation(stay, phase, signal) {
    await simulate(stay.guestCode, 'extras', signal)
    const reco = (stay.guestCode.includes('hotel') ? RECOMMENDATIONS_HOTEL : RECOMMENDATIONS)[phase] ?? null
    if (!reco) return null
    const dismissed = readLocal<string[]>(stay.guestCode, DISMISSED_FIELD) ?? []
    return dismissed.includes(reco.id) ? null : reco
  },
  dismissRecommendation(stay, id) {
    const dismissed = readLocal<string[]>(stay.guestCode, DISMISSED_FIELD) ?? []
    writeLocal(stay.guestCode, DISMISSED_FIELD, [...new Set([...dismissed, id])])
    return Promise.resolve()
  },
  async checkout(stay, items) {
    await simulate(stay.guestCode, 'extras')
    const g = stay.guestCode
    if (g.includes('extras-falla')) throw new ServiceError('server', 'mock checkout error')
    if (g.includes('pago-falla')) throw new ServiceError('payment_failed', 'card')
    const orders = items.map(orderFor)
    const total = orders.reduce((sum, o) => sum + o.amount.amount, 0)
    const id = `CHK-${Date.now()}`
    if (total === 0 || g.includes('pago-directo')) {
      saveCheckout(g, { id, orders, paid: true, temporalPaidId: null })
      return { mode: 'confirmed', checkoutId: id }
    }
    const temporalPaidId = `TMP-X-${Date.now()}`
    saveCheckout(g, { id, orders, paid: false, temporalPaidId })
    return { mode: 'redirect', checkoutId: id, amount: money(total, stay.currency), temporalPaidId, iframeUrl: `/dev/banco?pago=${encodeURIComponent(temporalPaidId)}&importe=${(total / 100).toFixed(2)}` }
  },
  async waitForCheckout(stay, checkoutId, { signal, timeoutMs = PAYMENT_WAIT_MS } = {}) {
    const g = stay.guestCode
    const checkout = findCheckout(g, checkoutId)
    if (!checkout) throw new ServiceError('server', 'checkout not found')
    const finish = (outcome: 'completed' | 'rejected') => {
      if (outcome === 'completed') saveCheckout(g, { ...checkout, paid: true })
      return outcome
    }
    if (checkout.paid) return 'completed'
    if (g.includes('pago-tiempo')) {
      await sleep(Math.min(timeoutMs, MOCK_SLOW_MS), signal)
      return 'timeout'
    }
    if (g.includes('pago-rechazado')) {
      await sleep(PAYMENT_POLL_MS, signal)
      return finish('rejected')
    }
    if (g.includes('pago-ok')) {
      await sleep(PAYMENT_POLL_MS, signal)
      return finish('completed')
    }
    const deadline = Date.now() + timeoutMs
    for (;;) {
      const decision = checkout.temporalPaidId ? readMockBankDecision(checkout.temporalPaidId) : null
      if (decision === 'completado') return finish('completed')
      if (decision === 'cancelado') return finish('rejected')
      if (Date.now() + PAYMENT_POLL_MS > deadline) return 'timeout'
      await sleep(PAYMENT_POLL_MS, signal)
    }
  },
  async getCheckout(stay, checkoutId, signal): Promise<ExtrasCheckoutSummary> {
    await simulate(stay.guestCode, 'extras', signal)
    const checkout = findCheckout(stay.guestCode, checkoutId)
    if (!checkout || !checkout.paid) throw new ServiceError('server', 'checkout not found')
    return { checkoutId, orders: checkout.orders, receiptsEmail: RECEIPTS_EMAIL }
  },
}
